import React from 'react';

interface Signal {
  direction: string;
  previous_direction?: string;
  strike?: number;
  target?: number;
  stop_loss?: number;
  confidence?: number;
  spot_price?: number;
  reasoning?: string[];
  scores?: { total?: number };
  created_at?: string;
  direction_changed?: boolean;
  expiry_date?: string;
}

interface Props {
  symbol: string;
  signal: Signal | null;
  loading: boolean;
  refreshing?: boolean;
  marketStatus?: 'OPEN' | 'CLOSED' | 'PRE';
  onRefresh: () => void;
}

const directionStyle: Record<string, React.CSSProperties> = {
  CE: { background: 'linear-gradient(135deg, #0d3320 0%, #1a5c3a 100%)', borderColor: '#26a65b' },
  PE: { background: 'linear-gradient(135deg, #3d0d0d 0%, #6b1a1a 100%)', borderColor: '#e74c3c' },
  WAIT: { background: 'linear-gradient(135deg, #1a1a2e 0%, #2d2d44 100%)', borderColor: '#f39c12' },
};

const directionLabel: Record<string, string> = {
  CE: '▲ BUY CALL (CE)',
  PE: '▼ BUY PUT (PE)',
  WAIT: '⏸ WAIT / HOLD',
};

const directionColor: Record<string, string> = {
  CE: '#26a65b',
  PE: '#e74c3c',
  WAIT: '#f39c12',
};

export default function AIDecisionPanel({ symbol, signal, loading, refreshing = false, marketStatus = 'CLOSED', onRefresh }: Props) {
  const dir = signal?.direction || 'WAIT';
  const style = directionStyle[dir] || directionStyle.WAIT;
  const color = directionColor[dir] || directionColor.WAIT;

  const confidence = signal?.confidence || 0;
  const filledDots = confidence;
  const emptyDots = 10 - confidence;

  const formatTime = (iso?: string) => {
    if (!iso) return '';
    return new Date(iso).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' });
  };

  // NIFTY weekly expiry = Tuesday (2), BANKNIFTY weekly expiry = Thursday (4)
  const getExpiryCountdown = () => {
    const now = new Date(new Date().toLocaleString('en-US', { timeZone: 'Asia/Kolkata' }));
    const target = symbol === 'BANKNIFTY' ? 4 : 2; // BANKNIFTY=Thu(4), NIFTY=Tue(2)
    const diff = (target - now.getDay() + 7) % 7 || 7;
    const expiry = new Date(now);
    expiry.setDate(now.getDate() + diff);
    const days = diff;
    const hours = Math.floor((expiry.getTime() - now.getTime()) / 3600000) % 24;
    return { days, hours, dateStr: expiry.toLocaleDateString('en-IN', { day: '2-digit', month: 'short' }) };
  };
  const exp = getExpiryCountdown();

  return (
    <div style={{
      ...style,
      border: `2px solid ${color}`,
      borderRadius: 8,
      padding: '1rem',
      position: 'relative',
      minHeight: 220,
    }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
        <span style={{ color: '#999', fontSize: '0.7rem', fontWeight: 700, letterSpacing: 1 }}>
          AI SIGNAL — {symbol}
        </span>
        {signal?.direction_changed && signal?.previous_direction && (
          <span style={{
            background: '#f39c12', color: '#000', fontSize: '0.6rem',
            padding: '2px 6px', borderRadius: 10, fontWeight: 700,
          }}>
            {signal.previous_direction} → {signal.direction}
          </span>
        )}
        {/* Expiry countdown */}
        <span style={{
          marginLeft: 'auto', color: exp.days <= 1 ? '#e74c3c' : '#555',
          fontSize: '0.6rem',
          fontWeight: exp.days <= 1 ? 700 : 400,
        }}>
          Expiry {exp.dateStr} · {exp.days}d {exp.hours}h
        </span>
      </div>

      {loading ? (
        <div style={{ textAlign: 'center', padding: '2rem', color: '#666' }}>
          <div style={{ fontSize: '1.5rem', animation: 'spin 1.2s linear infinite', display: 'inline-block' }}>⟳</div>
          <div style={{ fontSize: '0.75rem', marginTop: 4 }}>Loading signal...</div>
        </div>
      ) : signal ? (
        <>
          {/* Direction */}
          <div style={{ color, fontSize: '1.1rem', fontWeight: 800, marginBottom: 10 }}>
            {directionLabel[dir]}
          </div>

          {/* Trade details */}
          {dir !== 'WAIT' && signal.strike && (
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 6, marginBottom: 10 }}>
              {[
                { label: 'Strike', value: signal.strike },
                { label: 'Target', value: signal.target },
                { label: 'Stop Loss', value: signal.stop_loss },
              ].map(({ label, value }) => (
                <div key={label} style={{ background: 'rgba(0,0,0,0.3)', borderRadius: 5, padding: '5px 8px' }}>
                  <div style={{ color: '#888', fontSize: '0.6rem' }}>{label}</div>
                  <div style={{ color: '#ddd', fontSize: '0.85rem', fontWeight: 700 }}>{value?.toLocaleString('en-IN') || '—'}</div>
                </div>
              ))}
            </div>
          )}

          {/* Confidence */}
          <div style={{ marginBottom: 10 }}>
            <div style={{ color: '#888', fontSize: '0.6rem', marginBottom: 3 }}>
              CONFIDENCE: {confidence}/10
            </div>
            <div>
              {'●'.repeat(filledDots).split('').map((_, i) => (
                <span key={i} style={{ color, fontSize: '0.7rem', marginRight: 2 }}>●</span>
              ))}
              {'○'.repeat(emptyDots).split('').map((_, i) => (
                <span key={i} style={{ color: '#444', fontSize: '0.7rem', marginRight: 2 }}>○</span>
              ))}
            </div>
          </div>

          {/* Reasoning */}
          {signal.reasoning && signal.reasoning.length > 0 && (
            <div>
              {signal.reasoning.slice(0, 3).map((r, i) => (
                <div key={i} style={{ color: '#bbb', fontSize: '0.68rem', marginBottom: 3, paddingLeft: 8, borderLeft: `2px solid ${color}44` }}>
                  {r}
                </div>
              ))}
            </div>
          )}
        </>
      ) : (
        <div style={{ padding: '1.2rem 0' }}>
          {marketStatus === 'CLOSED' ? (
            <>
              <div style={{ fontSize: '1.8rem', textAlign: 'center', marginBottom: 6 }}>🌙</div>
              <div style={{ color: '#888', fontSize: '0.75rem', textAlign: 'center', fontWeight: 600 }}>
                Market Closed
              </div>
              <div style={{ color: '#555', fontSize: '0.67rem', textAlign: 'center', marginTop: 4, lineHeight: 1.5 }}>
                First signal computes at 9:00 AM IST<br />when market opens (Mon–Fri)
              </div>
            </>
          ) : marketStatus === 'PRE' ? (
            <>
              <div style={{ fontSize: '1.8rem', textAlign: 'center', marginBottom: 6 }}>⏳</div>
              <div style={{ color: '#f39c12', fontSize: '0.75rem', textAlign: 'center', fontWeight: 600 }}>
                Pre-Market
              </div>
              <div style={{ color: '#555', fontSize: '0.67rem', textAlign: 'center', marginTop: 4 }}>
                Signal cron starts at 9:00 AM IST
              </div>
            </>
          ) : (
            <>
              <div style={{ fontSize: '1.8rem', textAlign: 'center', marginBottom: 6 }}>📡</div>
              <div style={{ color: '#666', fontSize: '0.75rem', textAlign: 'center', fontWeight: 600 }}>
                Computing first signal...
              </div>
              <div style={{ color: '#555', fontSize: '0.67rem', textAlign: 'center', marginTop: 4 }}>
                Cron runs every 10 min · Check KITE + Grok API keys
              </div>
            </>
          )}
        </div>
      )}

      {/* Footer */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 10 }}>
        <span style={{ color: '#555', fontSize: '0.6rem' }}>
          {signal ? `Updated ${formatTime(signal.created_at)}` : ''}
        </span>
        <button onClick={onRefresh} disabled={refreshing} style={{
          background: 'transparent', border: `1px solid ${color}55`, color,
          fontSize: '0.65rem', padding: '3px 10px', borderRadius: 4,
          cursor: refreshing ? 'default' : 'pointer', opacity: refreshing ? 0.6 : 1,
        }}>
          {refreshing ? '⟳ Computing...' : '↺ Refresh'}
        </button>
      </div>
    </div>
  );
}
